// Abstraction layer for API calls
// This centralizes all API communication and handles errors consistently

import { Product, Order, Cart, StockValidationResult, Stock, DiscountValidationResponse, OpeningHours, ProductOption, OrderClientMetadata, DynamicDiscountRecord } from '../types';
import { getAdminPassword, getAdminHeader } from '../shared/authConfig';

export interface OrdersTableQuery {
    limit?: number;
    orderDateFrom?: string;
    orderDateTo?: string;
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
}

export interface RawOrderRow {
    id: string;
    customer_name: string;
    customer_email: string;
    customer_phone: string | null;
    address: string;
    postcode: string | null;
    number: string | null;
    street: string | null;
    area: string | null;
    zone: number | null;
    is_postcode_found: number | null;
    address_id: string | null;
    items_json: string;
    subtotal: number;
    total: number;
    status: string;
    order_date: string;
    delivery_date: string;
    deliver_now: number;
    delivery_notes: string | null;
    scheduled_slot: string | null;
    slot_was_bumped: number | null;
    original_requested_slot: string | null;
    payment_method: string | null;
    driver_tip_added: number | null;
    driver_tip_amount: number | null;
    receive_email_updates: number | null;
    is_test: number | null;
    is_paid: number | null;
    refund_amount: number | null;
    sumup_checkout_id: string | null;
    paypal_order_id: string | null;
    paypal_capture_id: string | null;
    payment_metadata_json: string | null;
    client_metadata_json: string | null;
    created_at: string;
    updated_at: string;
    discount_code: string | null;
    discount_percentage: number | null;
    discount_fixed_amount: number | null;
    discount_amount: number | null;
}

export interface OrdersTableResult {
    rows: RawOrderRow[];
    totalCount: number;
}

export interface ApiService {
    getProducts(availableOnly?: boolean): Promise<Product[]>;
    getProduct(id: string): Promise<Product>;
    createProduct(product: Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>): Promise<Product>;
    updateProduct(id: string, product: Partial<Product>): Promise<Product>;
    deleteProduct(id: string): Promise<void>;
    duplicateProduct(id: string): Promise<Product>;
    getStocks(): Promise<Stock[]>;
    createStock(stock: Omit<Stock, 'id' | 'updatedAt'>): Promise<Stock>;
    updateStock(id: string, stock: Partial<Stock>): Promise<Stock>;
    deleteStock(id: string): Promise<void>;
    getCommonOptions(): Promise<ProductOption[]>;
    getCommonOption(id: string): Promise<ProductOption>;
    createCommonOption(option: ProductOption): Promise<ProductOption>;
    updateCommonOption(id: string, option: ProductOption): Promise<ProductOption>;
    deleteCommonOption(id: string): Promise<{ success: boolean; message: string; usedByProducts?: string[] }>;
    propagateCommonOption(id: string, option: ProductOption): Promise<{ updated: number; productNames: string[] }>;
    getCommonOptionUsage(id: string): Promise<{ productNames: string[]; count: number }>;
    createOrder(order: Omit<Order, 'id'>): Promise<Order>;
    getOrderById(id: string): Promise<Order>;
    getOrders(): Promise<Order[]>;
    getOrdersTable(query?: OrdersTableQuery): Promise<OrdersTableResult>;
    updateOrderField(orderId: string, field: string, value: unknown): Promise<{ success: boolean; message: string }>;
    getKitchenOrders(date: string, statuses: string[]): Promise<Order[]>;
    deleteOrder(id: string): Promise<void>;
    getOpeningHours(): Promise<OpeningHours>;
    updateOpeningHours(hours: OpeningHours): Promise<OpeningHours>;
    validateCart(cart: Cart): Promise<StockValidationResult>;
    validateDiscount(code: string, subtotal: number): Promise<DiscountValidationResponse>;
    getDynamicDiscounts(): Promise<DynamicDiscountRecord[]>;
    createDynamicDiscount(discount: { code: string; percentage: number; fixedAmount?: number; minOrderOverride?: number; maxUses: number; message: string; adminNote: string; expiresAt?: string }): Promise<DynamicDiscountRecord>;
    deleteDynamicDiscount(code: string): Promise<{ success: boolean; message: string }>;
    checkDynamicDiscountExists(code: string): Promise<{ code: string; exists: boolean }>;
    trackVisit(visitorId: string, metadata?: Partial<OrderClientMetadata>): Promise<{ success: boolean }>;
    healthCheck(): Promise<{ status: string; timestamp: string }>;
    getEnvironment(): Promise<{ environment: string }>;
}

class RestaurantApiService implements ApiService {
    private baseUrl: string;

    // Endpoints that require admin auth for all write operations
    private readonly ADMIN_WRITE_ENDPOINTS = [
        '/products',
        '/stocks',
        '/common-options',
        '/opening-hours',
        '/discounts',
    ];

    // Public endpoints (customer operations)
    private readonly PUBLIC_ENDPOINTS = [
        '/orders', // POST (place order) and PUT (update after payment) are public
    ];

    constructor(baseUrl: string = '/api') {
        this.baseUrl = baseUrl;
    }

    /**
     * Determines if an endpoint requires admin authentication
     */
    private isAdminEndpoint(endpoint: string, method: string = 'GET'): boolean {
        // Check if this is a public endpoint
        const isPublic = this.PUBLIC_ENDPOINTS.some(publicPath => endpoint.startsWith(publicPath));

        if (isPublic) {
            // For orders: GET, DELETE, and PATCH require auth (viewing/deleting/editing orders)
            // POST (placing order) and PUT (updating status after payment) are public
            if (endpoint.startsWith('/orders') && (method === 'GET' || method === 'DELETE' || method === 'PATCH')) {
                return true; // GET, DELETE, and PATCH require admin auth
            }
            return false; // POST and PUT are public
        }

        // Check endpoints that require auth for all write operations
        if (method !== 'GET') {
            const requiresWriteAuth = this.ADMIN_WRITE_ENDPOINTS.some(adminPath =>
                endpoint.startsWith(adminPath)
            );
            if (requiresWriteAuth) {
                return true;
            }
        }

        return false;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;
        const method = options.method || 'GET';

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string>),
        };

        // Add admin auth header for admin endpoints using centralized config
        if (this.isAdminEndpoint(endpoint, method)) {
            const adminPass = getAdminPassword();
            const adminHeader = getAdminHeader();
            console.log(`[API] Admin endpoint detected: ${method} ${endpoint}`);
            console.log(`[API] Admin password from storage:`, adminPass ? '***EXISTS***' : 'NOT FOUND');

            if (adminPass) {
                headers[adminHeader] = adminPass;
                console.log(`[API] Admin auth header added`);
            } else {
                console.warn(`[API] Admin password not found. Please log in to admin panel.`);
            }
        }

        const defaultOptions: RequestInit = {
            headers,
        };

        try {
            const response = await fetch(url, { ...defaultOptions, ...options });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({})) as any;
                throw new Error(
                    errorData.error || `HTTP ${response.status}: ${response.statusText}`
                );
            }

            return await response.json();
        } catch (error) {
            console.error(`API request failed: ${url}`, error);
            throw error;
        }
    }

    async getProducts(availableOnly: boolean = false): Promise<Product[]> {
        const queryParam = availableOnly ? '?available=true' : '';
        return this.request<Product[]>(`/products${queryParam}`);
    }

    async getProduct(id: string): Promise<Product> {
        return this.request<Product>(`/products/${id}`);
    }

    async createProduct(product: Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>): Promise<Product> {
        return this.request<Product>('/products', {
            method: 'POST',
            body: JSON.stringify(product),
        });
    }

    async updateProduct(id: string, product: Partial<Product>): Promise<Product> {
        return this.request<Product>(`/products/${id}`, {
            method: 'PUT',
            body: JSON.stringify(product),
        });
    }

    async deleteProduct(id: string): Promise<void> {
        await this.request(`/products/${id}`, {
            method: 'DELETE',
        });
    }

    async duplicateProduct(id: string): Promise<Product> {
        return this.request<Product>(`/products/${id}/duplicate`, {
            method: 'POST',
        });
    }

    async getStocks(): Promise<Stock[]> {
        return this.request<Stock[]>('/stocks');
    }

    async createStock(stock: Omit<Stock, 'id' | 'updatedAt'>): Promise<Stock> {
        return this.request<Stock>('/stocks', {
            method: 'POST',
            body: JSON.stringify(stock),
        });
    }

    async updateStock(id: string, stock: Partial<Stock>): Promise<Stock> {
        return this.request<Stock>(`/stocks/${id}`, {
            method: 'PUT',
            body: JSON.stringify(stock),
        });
    }

    async deleteStock(id: string): Promise<void> {
        await this.request(`/stocks/${id}`, {
            method: 'DELETE',
        });
    }

    async getCommonOptions(): Promise<ProductOption[]> {
        return this.request<ProductOption[]>('/common-options');
    }

    async getCommonOption(id: string): Promise<ProductOption> {
        return this.request<ProductOption>(`/common-options/${id}`);
    }

    async createCommonOption(option: ProductOption): Promise<ProductOption> {
        return this.request<ProductOption>('/common-options', {
            method: 'POST',
            body: JSON.stringify(option),
        });
    }

    async updateCommonOption(id: string, option: ProductOption): Promise<ProductOption> {
        return this.request<ProductOption>(`/common-options/${id}`, {
            method: 'PUT',
            body: JSON.stringify(option),
        });
    }

    async deleteCommonOption(id: string): Promise<{ success: boolean; message: string; usedByProducts?: string[] }> {
        return this.request<{ success: boolean; message: string; usedByProducts?: string[] }>(`/common-options/${id}`, {
            method: 'DELETE',
        });
    }

    async propagateCommonOption(id: string, option: ProductOption): Promise<{ updated: number; productNames: string[] }> {
        return this.request<{ updated: number; productNames: string[] }>(`/common-options/${id}/propagate`, {
            method: 'POST',
            body: JSON.stringify(option),
        });
    }

    async getCommonOptionUsage(id: string): Promise<{ productNames: string[]; count: number }> {
        return this.request<{ productNames: string[]; count: number }>(`/common-options/${id}/usage`);
    }

    async createOrder(order: Omit<Order, 'id'>): Promise<Order> {
        return this.request<Order>('/orders', {
            method: 'POST',
            body: JSON.stringify(order),
        });
    }

    async getOrders(): Promise<Order[]> {
        return this.request<Order[]>('/orders');
    }

    async getOrdersTable(query: OrdersTableQuery = {}): Promise<OrdersTableResult> {
        const params = new URLSearchParams({ view: 'table' });
        if (query.limit !== undefined) params.set('limit', query.limit.toString());
        if (query.orderDateFrom) params.set('orderDateFrom', query.orderDateFrom);
        if (query.orderDateTo) params.set('orderDateTo', query.orderDateTo);
        if (query.deliveryDateFrom) params.set('deliveryDateFrom', query.deliveryDateFrom);
        if (query.deliveryDateTo) params.set('deliveryDateTo', query.deliveryDateTo);
        return this.request<OrdersTableResult>(`/orders?${params.toString()}`);
    }

    async updateOrderField(orderId: string, field: string, value: unknown): Promise<{ success: boolean; message: string }> {
        return this.request<{ success: boolean; message: string }>(`/orders/${orderId}`, {
            method: 'PATCH',
            body: JSON.stringify({ [field]: value }),
        });
    }

    async getOrderById(id: string): Promise<Order> {
        return this.request<Order>(`/orders/${id}`);
    }

    async getKitchenOrders(date: string, statuses: string[]): Promise<Order[]> {
        const params = new URLSearchParams({
            date,
            statuses: statuses.join(',')
        });
        return this.request<Order[]>(`/orders?${params.toString()}`);
    }

    async deleteOrder(id: string): Promise<void> {
        await this.request(`/orders/${id}`, {
            method: 'DELETE',
        });
    }

    async getOpeningHours(): Promise<OpeningHours> {
        return this.request<OpeningHours>('/opening-hours');
    }

    async updateOpeningHours(hours: OpeningHours): Promise<OpeningHours> {
        return this.request<OpeningHours>('/opening-hours', {
            method: 'PUT',
            body: JSON.stringify(hours),
        });
    }

    async validateCart(cart: Cart): Promise<StockValidationResult> {
        return this.request<StockValidationResult>('/cart/validate', {
            method: 'POST',
            body: JSON.stringify(cart),
        });
    }

    async validateDiscount(code: string, subtotal: number): Promise<DiscountValidationResponse> {
        return this.request<DiscountValidationResponse>('/discounts/validate', {
            method: 'POST',
            body: JSON.stringify({ code, subtotal }),
        });
    }

    async getDynamicDiscounts(): Promise<DynamicDiscountRecord[]> {
        return this.request<DynamicDiscountRecord[]>('/discounts');
    }

    async createDynamicDiscount(discount: { code: string; percentage: number; fixedAmount?: number; minOrderOverride?: number; maxUses: number; message: string; adminNote: string; expiresAt?: string }): Promise<DynamicDiscountRecord> {
        return this.request<DynamicDiscountRecord>('/discounts', {
            method: 'POST',
            body: JSON.stringify(discount),
        });
    }

    async deleteDynamicDiscount(code: string): Promise<{ success: boolean; message: string }> {
        return this.request<{ success: boolean; message: string }>(`/discounts/${encodeURIComponent(code)}`, {
            method: 'DELETE',
        });
    }

    async checkDynamicDiscountExists(code: string): Promise<{ code: string; exists: boolean }> {
        return this.request<{ code: string; exists: boolean }>(`/discounts/check/${encodeURIComponent(code)}`);
    }

    async trackVisit(visitorId: string, metadata?: Partial<OrderClientMetadata>): Promise<{ success: boolean }> {
        return this.request<{ success: boolean }>('/visits', {
            method: 'POST',
            body: JSON.stringify({
                visitorId,
                url: window.location.href,
                referrer: document.referrer || undefined,
                metadata
            }),
        });
    }

    async healthCheck(): Promise<{ status: string; timestamp: string }> {
        return this.request<{ status: string; timestamp: string }>('/health');
    }

    async getEnvironment(): Promise<{ environment: string }> {
        return this.request<{ environment: string }>('/environment');
    }
}

// Create singleton instance
export const apiService: ApiService = new RestaurantApiService();

// For testing - allows injection of mock API
export const createApiService = (baseUrl?: string): ApiService => {
    return new RestaurantApiService(baseUrl);
};

// ============================================================================
// Headsup Notification System (Fire-and-forget, non-critical)
// ============================================================================

/**
 * Headsup event types
 */
export type HeadsupEventType = 'returning_user' | 'basket_changed' | 'checkout_launched' | 'tab_switched' | 'tab_closed' | 'tab_returned' | 'order_placed_summary';

/**
 * Cart item type for headsup - accepts both shared and frontend CartItem structures
 */
interface HeadsupCartItem {
    product: {
        id: string;
        name: string;
        price: number;
    };
    quantity: number;
    totalPrice: number;
    selectedOptions?: {
        optionId: string;
        choiceIds: string[];
    }[];
}

/**
 * Headsup payload for sending notifications
 */
export interface HeadsupPayload {
    eventType: HeadsupEventType;
    visitorId?: string;
    customerName?: string;
    customerAddress?: string;
    cartItems?: HeadsupCartItem[];
    visitCount?: number;
}

/**
 * Send a headsup notification to the backend.
 * This is a fire-and-forget function - failures are silently logged.
 * Skips sending for admin users and non-production environments.
 * 
 * @param payload - The headsup event data
 */
export function sendHeadsup(payload: HeadsupPayload): void {
    try {
        // Only send headsup in production
        const isProduction = window.location.hostname === 'delifeed.com' || window.location.hostname === 'www.delifeed.com';
        if (!isProduction) {
            console.log('[Headsup] Non-production environment, skipping headsup notification');
            return;
        }

        // Skip for admin users
        const isAdmin = localStorage.getItem('admin-pass');
        if (isAdmin) {
            console.log('[Headsup] Admin detected, skipping headsup notification');
            return;
        }

        // Fire and forget - don't await
        fetch('/api/telegram/headsup', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })
            .then(response => {
                if (response.ok) {
                    console.log(`[Headsup] Notification sent: ${payload.eventType}`);
                } else {
                    console.log(`[Headsup] Notification failed (${response.status}), continuing...`);
                }
            })
            .catch(error => {
                console.log('[Headsup] Notification error, continuing...', error);
            });
    } catch (error) {
        // Silently ignore any errors - headsup is non-critical
        console.log('[Headsup] Error preparing notification, continuing...', error);
    }
}
